/**
 * When has a tenant genuinely finished onboarding?
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────────────────
 * The onboarding router used to send a tenant to "Happy Move-In" the moment the EMANDATE
 * step read SUCCESS:
 *
 *     } else if (emandateData?.status === "SUCCESS") {   // -> happy-move-in
 *
 * That branch sat ABOVE the branches that check KYC / banking / penny-drop / e-sign, so one
 * step decided the whole journey — an OR where an AND was required. When a backend bug marked
 * EMANDATE complete on a journey the tenant had not started, the tenant was shown a
 * congratulations screen and, worse, could no longer reach the steps they still owed.
 * Measured on production 2026-08-07: 53 open journeys in exactly that state.
 *
 * The backend cause is fixed separately (cpcore `mandate-attribution.ts`). This is the second
 * line of defence: even if the API says one step is complete, the UI must not declare the
 * whole journey complete unless every step actually is.
 *
 * ── DESIGN NOTES ────────────────────────────────────────────────────────────────────────
 * • Completion is derived from the journey's OWN `steps` array rather than a hardcoded list
 *   of names, so partner- or product-specific step sets keep working without edits here.
 * • Comparison is case-insensitive on purpose. Production contains a step whose status is
 *   `SUCESS` (a typo, journey CPJ2502131037XF3D4); a strict `=== "SUCCESS"` check would
 *   strand that tenant permanently. Case-insensitivity does not rescue that particular typo,
 *   but it does cover the real `ACTIVE`/`Active` style drift already present in this data.
 * • Fails CLOSED: no steps, an empty array, or anything unexpected means "not complete". The
 *   cost of a false negative is a tenant who sees their (already finished) step list once
 *   more; the cost of a false positive is a tenant locked out of their own onboarding.
 */

/** The only status that counts as done. */
const SUCCESS = 'SUCCESS';

/**
 * Is one journey step finished?
 *
 * Exported because the router also needs to ask about individual steps, and it must ask the
 * same question the same way everywhere — two slightly different notions of "done" in one
 * file is how the original bug survived review.
 */
export function isStepComplete(step) {
  return typeof step?.status === 'string' && step.status.toUpperCase() === SUCCESS;
}

/** Find a step by name, whatever case the backend used. */
export function findStep(steps, name) {
  if (!Array.isArray(steps)) return undefined;
  return steps.find(
    (step) => typeof step?.name === 'string' && step.name.toUpperCase() === name.toUpperCase(),
  );
}

/**
 * Has EVERY step of this journey completed?
 *
 * This is the only condition under which the tenant may be routed to Happy Move-In.
 *
 * @param {Array<{name?: string, status?: string}>} steps the journey's own steps array
 * @returns {boolean}
 */
export function areAllStepsComplete(steps) {
  // Fail closed on a missing or empty step list — "we do not know" is not "finished".
  if (!Array.isArray(steps) || steps.length === 0) return false;

  return steps.every(isStepComplete);
}

/**
 * The steps still outstanding, for logging and for showing the tenant what is left.
 *
 * @returns {string[]} step names, in the journey's own order
 */
export function pendingStepNames(steps) {
  if (!Array.isArray(steps)) return [];

  return steps.filter((step) => !isStepComplete(step)).map((step) => step?.name).filter(Boolean);
}
